import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { trackWhatsAppContact, trackRentalCarReservation } from "../helper/facebookPixel";

const RentalCarDetails = ({ car }) => {
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!car) return null;

  const {
    title,
    subtitle,
    year,
    fuel,
    transmission,
    seats,
    features,
    description,
    images,
    amovensUrl,
    dailyRate,
    pickupLocation,
  } = car;

  const openLightbox = () => {
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  const navigateImage = (direction) => {
    if (direction === 'prev') {
      setActiveImage(prev => prev === 0 ? images.length - 1 : prev - 1);
    } else {
      setActiveImage(prev => prev === images.length - 1 ? 0 : prev + 1);
    }
  };

  return (
    <div className="rental-details">
      <Helmet>
        <title>{title} - Alquiler de Coches | Leal Taller Sevilla</title>
        <meta name="description" content={`Alquila el ${title} en Sevilla. ${subtitle}. Reserva fácil a través de Amovens.`} />
        <meta property="og:title" content={`${title} - Alquiler | Leal Taller`} />
        <meta property="og:description" content={`${subtitle}. ${description?.substring(0, 150)}...`} />
        <meta property="og:image" content={images?.[0]} />
        <link rel="canonical" href={`https://www.lealchapaypintura.com/alquiler/${car.slug}`} />

        {/* Vehicle Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Car",
            "name": title,
            "description": description,
            "vehicleModelDate": year,
            "fuelType": fuel,
            "vehicleTransmission": transmission,
            "seatingCapacity": seats?.replace(/\D/g, ''),
            "image": images,
            "offers": {
              "@type": "Offer",
              "url": amovensUrl,
              "availability": "https://schema.org/InStock",
              "seller": {
                "@type": "AutoRepair",
                "name": "Leal Chapa y Pintura"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="container">
        <div className="rental-details__grid">
          {/* Main Content */}
          <div className="rental-details__main">
            {/* Image Gallery */}
            <div className="rental-details__gallery">
              <div className="rental-details__main-image" onClick={openLightbox}>
                <img
                  src={images?.[activeImage]}
                  alt={`${title} - Imagen ${activeImage + 1}`}
                />

                {/* Navigation arrows */}
                {images?.length > 1 && (
                  <>
                    <button
                      className="rental-details__nav rental-details__nav--prev"
                      onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <button
                      className="rental-details__nav rental-details__nav--next"
                      onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                    <div className="rental-details__image-counter">
                      {activeImage + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {images?.length > 1 && (
                <div className="rental-details__thumbnails">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      className={`rental-details__thumbnail ${idx === activeImage ? 'active' : ''}`}
                      onClick={() => setActiveImage(idx)}
                    >
                      <img src={img} alt={`${title} - Miniatura ${idx + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="rental-details__section">
              <h2 className="rental-details__section-title">Descripción</h2>
              <p className="rental-details__description">{description}</p>

              {features?.length > 0 && (
                <div className="rental-details__features-section">
                  <strong className="rental-details__features-label">Equipamiento:</strong>
                  <div className="rental-details__features">
                    {features.map((feature, idx) => (
                      <span key={idx} className="rental-details__feature">{feature}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="rental-details__sidebar">
            <div className="rental-details__booking-card">
              {/* Header with price */}
              <div className="rental-details__car-header">
                <h1 className="rental-details__car-title">{title}</h1>
                <p className="rental-details__car-subtitle">{subtitle}</p>
                {dailyRate && (
                  <div className="rental-details__price">
                    <span className="rental-details__price-label">desde</span>
                    <span className="rental-details__price-value">{dailyRate}€</span>
                    <span className="rental-details__price-period">/día</span>
                  </div>
                )}
              </div>

              {/* Location */}
              {pickupLocation && (
                <div className="rental-details__location">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>{pickupLocation}</span>
                </div>
              )}

              {/* Quick specs as compact bubbles */}
              <div className="rental-details__specs">
                <span className="rental-details__spec"><i className="fas fa-calendar-alt"></i> {year}</span>
                <span className="rental-details__spec"><i className="fas fa-gas-pump"></i> {fuel}</span>
                <span className="rental-details__spec"><i className="fas fa-cog"></i> {transmission}</span>
                <span className="rental-details__spec"><i className="fas fa-users"></i> {seats}</span>
              </div>

              {/* CTA Buttons */}
              <div className="rental-details__cta">
                <a
                  href={amovensUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rental-details__btn rental-details__btn--primary"
                  onClick={() => trackRentalCarReservation(title, car.slug, amovensUrl)}
                >
                  Reservar en Amovens
                </a>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="rental-details__lightbox" onClick={closeLightbox}>
          <button className="rental-details__lightbox-close" onClick={closeLightbox}>
            <i className="fas fa-times"></i>
          </button>
          <button
            className="rental-details__lightbox-nav rental-details__lightbox-nav--prev"
            onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          <img
            src={images?.[activeImage]}
            alt={`${title} - Imagen ampliada`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="rental-details__lightbox-nav rental-details__lightbox-nav--next"
            onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
          <div className="rental-details__lightbox-counter">
            {activeImage + 1} / {images?.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalCarDetails;
